import { FileCheck2, Network, ShieldCheck, RefreshCw, Landmark, ClipboardList } from "lucide-react";

export const NAV = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Packages", href: "#packages" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const FAQS = [
  {
    q: "What is provider enrollment?",
    a: [
      "Provider enrollment is the process of applying to an insurance payer so a healthcare provider or practice can participate in the payer's network and, once approved and effective, submit claims for covered services. Enrollment may involve commercial insurance plans, Medicare, Medicaid, and other health plans.",
    ],
  },
  {
    q: "What's the difference between credentialing and enrollment?",
    a: [
      "Credentialing is the verification of a provider's professional qualifications, such as education, training, licenses, certifications, work history, malpractice coverage, and other required information.",
      "Enrollment is the process of submitting the provider or group to an insurance payer and completing the requirements needed to participate in the payer's network and bill under the appropriate contract.",
      "The two processes often work together, but they are not the same.",
    ],
  },
  {
    q: "What is CAQH credentialing?",
    a: [
      "CAQH ProView is a centralized provider data platform used by many health plans to collect information needed for credentialing and recredentialing. Providers maintain information such as licenses, education, work history, malpractice coverage, practice locations, and supporting documents within their CAQH profile.",
      "We can help establish, review, update, and maintain CAQH profiles so the information is accurate, complete, and ready for payer credentialing.",
    ],
  },
  {
    q: "How long does provider enrollment take?",
    a: [
      "Timelines vary significantly by payer, state, provider type, network availability, and whether the application is complete. Commercial payer enrollment commonly takes 60–120+ days, while Medicare and Medicaid timelines vary by program and jurisdiction.",
      "Because delays are common, proactive follow-up and accurate tracking are an important part of the enrollment process.",
    ],
  },
  {
    q: "Can I bill insurance while credentialing is pending?",
    a: [
      "Usually, you should not assume services can be billed as in-network until the payer confirms the provider's effective date and billing eligibility.",
      "Some payers allow retroactive effective dates or other arrangements, but policies vary. We recommend verifying the payer's requirements before providing services with the expectation of in-network reimbursement.",
    ],
  },
  {
    q: "When should we start credentialing a new provider?",
    a: [
      "Ideally, credentialing and enrollment should begin 90–120 days before the provider's anticipated start date whenever possible.",
      "Starting early allows time to collect documentation, update CAQH, submit applications, resolve payer requests, and account for processing delays.",
    ],
  },
  {
    q: "How much does medical credentialing cost?",
    a: [
      "Pricing depends on the size and complexity of the project, including the number of providers, payers, states, locations, and whether the practice needs commercial, Medicare, Medicaid, or multi-state enrollment.",
      "We provide both flat rates and customized pricing based on your actual enrollment needs so you're paying for the level of support your organization requires.",
    ],
  },
  {
    q: "Do you require a long-term contract?",
    a: [
      "Not necessarily. We can support practices through project-based engagements or ongoing credentialing and enrollment management, depending on your needs.",
      "Whether you're enrolling one provider with a few payers or managing a growing multi-state organization, we can structure services around your scope and goals.",
    ],
  },
  {
    q: "What happens if my credentials lapse?",
    a: [
      "Expired licenses, malpractice coverage, certifications, CAQH attestations, or other required credentials can create credentialing and enrollment issues and may affect payer participation.",
      "Ongoing monitoring helps identify upcoming expirations early so updates can be completed before they become a problem.",
    ],
  },
  {
    q: "Do I need to re-credential with every payer?",
    a: [
      "Most payers require providers to undergo periodic recredentialing, often approximately every three years, although requirements and timelines vary by payer.",
      "Keeping provider information, CAQH profiles, licenses, insurance, and other credentials current can make recredentialing much smoother.",
    ],
  },
  {
    q: "Do you keep our enrollments active after approval?",
    a: [
      "Yes. Ongoing support can include monitoring recredentialing and revalidation requirements, CAQH attestations, expiring credentials, payer demographic updates, roster maintenance, and other enrollment-related deadlines.",
      "The goal isn't simply to get your practice enrolled, it's to help keep your payer participation organized and compliant as your organization grows.",
    ],
  },
  {
    q: "Do you outsource any of the work?",
    a: [
      "No, credentialing and enrollment work is handled through our team utilizing established processes with an emphasis on accuracy, confidentiality, accountability, and secure handling of provider information.",
      "If outside resources are ever required for a particular engagement, we believe in full transparency. We will always communicate with you in advance and discuss any external resources before moving forward, rather than making those decisions on your behalf.",
    ],
  },
  {
    q: "What kinds of practices do you work with?",
    a: [
      "We support individual providers, group practices, startups, growing healthcare organizations, telehealth practices, behavioral health organizations, and multi-state medical groups.",
      "Our services can scale from helping a new practice establish its initial payer relationships to building and managing credentialing and enrollment workflows for organizations expanding across multiple states and payer networks.",
    ],
  },
];

export const SERVICES = [
  {
    icon: FileCheck2,
    title: "Provider Enrollment",
    desc: "End-to-end enrollment of individual and group providers across commercial and government payers.",
    span: "md:col-span-7",
  },
  {
    icon: Network,
    title: "Payer Contracting",
    desc: "Negotiation and execution of participation agreements to expand your in-network reach.",
    span: "md:col-span-5",
  },
  {
    icon: ShieldCheck,
    title: "Credentialing & CAQH",
    desc: "Documentation reviews and meticulous CAQH profile management kept audit-ready.",
    span: "md:col-span-5",
  },
  {
    icon: Landmark,
    title: "Medicare & Medicaid",
    desc: "PECOS, revalidations, and state Medicaid enrollment services offered.",
    span: "md:col-span-7",
  },
  {
    icon: RefreshCw,
    title: "Revalidation & Maintenance",
    desc: "Proactive tracking of expirables, re-attestations, and revalidation deadlines.",
    span: "md:col-span-6",
  },
  {
    icon: ClipboardList,
    title: "Roster & Demographic Updates",
    desc: "Accurate demographic changes, add/terms, and roster reconciliation across all plans.",
    span: "md:col-span-6",
  },
];

export const PROCESS = [
  { no: "01", title: "Discovery & Audit", desc: "We map every provider, payer, and expirable to build a single source of truth for your organization." },
  { no: "02", title: "Application & Submission", desc: "Applications are prepared, verified, and submitted with obsessive attention to payer-specific rules." },
  { no: "03", title: "Follow-Up & Escalation", desc: "Relentless payer follow-up with documented touchpoints until every effective date is confirmed." },
  { no: "04", title: "Maintenance & Reporting", desc: "Continuous monitoring, transparent status reporting, and revalidation ahead of every deadline." },
];

export const PACKAGES = [
  {
    name: "Essentials",
    tag: "Solo & Small Practice",
    price: "$499",
    unit: "/ provider",
    features: [
      "Single provider enrollment",
      "CAQH setup & attestation",
      "Up to 5 payer applications",
      "Status reporting",
      "Email support",
    ],
    featured: false,
  },
  {
    name: "Group Practice",
    tag: "Most Popular",
    price: "$1,450",
    unit: "/ month",
    features: [
      "Up to 5 providers",
      "Up to 3 payers",
      "CAQH setup & attestation",
      "Payer contracting support",
      "Revalidation & maintenance",
      "Status reporting",
      "Email support",
    ],
    featured: true,
  },
  {
    name: "Need Something Else",
    tag: "Health Systems",
    price: "Custom",
    unit: "tailored scope",
    features: [
      "Unlimited providers & locations",
      "Full delegated and non-delegated credentialing",
      "Roster & demographic management",
      "Dedicated account team",
      "Maintenance and Follow Ups",
      "Other credentialing and enrollment services",
    ],
    featured: false,
  },
];
